/**
 * Customer-field validation, shared between the client and both servers.
 *
 * RUNTIME CONSTRAINT: plain ESM, zero imports, no Node built-ins. The Cloudflare Pages
 * Function ships as source with no bundler step in this repo, so a dependency here (zod, yup)
 * would mean introducing a build pipeline for functions/ -- a bigger change than the
 * validation it serves. All validation sits behind this one module boundary, so swapping to a
 * schema library later is contained.
 *
 * The server previously did NO validation at all: handleCreateCheckoutSession destructured
 * email/firstName/lastName and passed them straight to Stripe and Mailchimp.
 */

/**
 * Person names.
 *
 * THE BUG THIS FIXES: the old regex was /^[a-zA-Z\s\-']+$/, which rejects "Renée", "José",
 * "Nguyễn", and "Müller" -- real customers, blocked from checking out, reading an error that
 * said "letters only".
 *
 * \p{L} matches any Unicode letter and \p{M} any combining mark; the latter matters because
 * "é" may be encoded as "e" + U+0301 rather than a single codepoint. Both require the `u`
 * flag. Anchoring the first character to a letter is what actually rejects junk input --
 * enumerating permitted letters never worked.
 *
 * Allows internal spaces, hyphens, apostrophes (straight and typographic), and periods for
 * initials.
 */
const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M}\s'’\-.]*$/u;

const MAX_NAME = 100;
const MAX_EMAIL = 254; // RFC 5321 practical maximum
const MAX_GENERIC = 200;

/** @returns {?string} an error message, or null when valid. */
export function validateFirstName(value) {
  return validatePersonName(value, 'first name');
}

/** @returns {?string} an error message, or null when valid. */
export function validateLastName(value) {
  return validatePersonName(value, 'last name');
}

function validatePersonName(value, label) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return `Please enter your ${label}`;
  if (trimmed.length > MAX_NAME) return `That ${label} is too long`;
  // Note the copy no longer claims "letters only", which was both wrong and unhelpful.
  if (!NAME_RE.test(trimmed)) return `Please enter a valid ${label}`;
  return null;
}

/**
 * Email. Deliberately permissive: the only authoritative test of an address is delivering to
 * it, and over-strict patterns reject valid addresses (plus-addressing, new TLDs, quoted
 * local parts). This catches typos and obvious junk, nothing more.
 */
export function validateEmail(value) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return 'Please enter your email address';
  if (trimmed.length > MAX_EMAIL) return 'That email address is too long';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Please enter a valid email address';
  return null;
}

/** US/Canada 10-digit phone, matching the input mask and the business. */
export function validatePhone(value) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return 'Phone number is required';
  if (countDigits(trimmed) !== 10) return 'Please enter a valid 10-digit phone number';
  return null;
}

/**
 * Website. Optional and intentionally permissive (a deliberate product decision).
 *
 * The old validateURL() returned true for any non-empty string while its error message read
 * "Website cannot be empty" -- so the only case it could reject was the one case that is
 * actually fine. This drops that pretense: blank is valid, and only a length cap applies.
 */
export function validateWebsite(value) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null; // optional
  if (trimmed.length > MAX_GENERIC) return 'That website address is too long';
  return null;
}

/** Company name. Optional, length-capped only. */
export function validateCompanyName(value) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  if (trimmed.length > MAX_GENERIC) return 'That company name is too long';
  return null;
}

/** Strip formatting and count digits. */
export function countDigits(value) {
  return (value ?? '').replace(/\D/g, '').length;
}

/** Digits only, capped at 10 -- the canonical stored form of a phone number. */
export function normalizePhone(value) {
  return (value ?? '').replace(/\D/g, '').slice(0, 10);
}

/** Format 10 digits as (555) 555-5555, progressively as the user types. */
export function formatPhone(value) {
  const d = normalizePhone(value);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Prepend https:// so a bare "example.com" becomes a clickable URL for the CRM. */
export function normalizeWebsite(value) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const VALIDATORS = {
  firstName: validateFirstName,
  lastName: validateLastName,
  email: validateEmail,
  phone: validatePhone,
  customerWebsite: validateWebsite,
  companyName: validateCompanyName,
};

/** Validate one field by name. Unknown fields are treated as valid. */
export function validateField(fieldName, value) {
  const fn = VALIDATORS[fieldName];
  return fn ? fn(value) : null;
}

/**
 * Validate the whole customer object.
 * @returns {{ok: boolean, errors: Record<string,string>}}
 */
export function validateCustomer(customer = {}) {
  const errors = {};

  Object.keys(VALIDATORS).forEach((field) => {
    const message = VALIDATORS[field](customer[field]);
    if (message) errors[field] = message;
  });

  return { ok: Object.keys(errors).length === 0, errors };
}
